import { IS_PROTOCOLS_ENABLED } from '@/app-shell/flags/evidence-flags';
import { getCurrentProtocols } from '@/hooks/query/useQueryProtocols';
import {
  TypographyCaption,
  TypographyCaptionBold,
} from '@heidi/design-system/typography';
import { type Editor } from '@tiptap/react';
import { Zap } from 'lucide-react';
import { type Command, type Group } from './types';

export interface ProtocolActionCreator {
  (editor: Editor, shorthand: string): void;
}

export interface CreateProtocolGroupOptions {
  editor: Editor;
  createInsertAction: ProtocolActionCreator;
}

/**
 * Creates a PROTOCOLS group for the slash command menu.
 * Protocols are AI skills that inject prompts when triggered with /shorthand.
 */
export function createProtocolGroup({
  editor,
  createInsertAction,
}: CreateProtocolGroupOptions): Group | null {
  // Check feature flag
  if (!IS_PROTOCOLS_ENABLED()) {
    return null;
  }

  const protocolsData = getCurrentProtocols();

  // Combine system and user protocols
  const allProtocols = [
    ...(protocolsData?.system_protocols ?? []),
    ...(protocolsData?.user_protocols ?? []),
  ];

  if (allProtocols.length === 0) {
    return null;
  }

  const protocolCommands: Command[] = allProtocols.map((protocol) => ({
    name: `protocol-${protocol.shorthand}`,
    label: protocol.name,
    description: protocol.description,
    icon: Zap,
    action: () => createInsertAction(editor, protocol.shorthand),
    content: (
      <div className="flex w-full min-w-0 max-w-[calc(100%-28px)] items-center gap-2">
        <TypographyCaptionBold className="inline-block w-fit max-w-[50%] shrink-0 truncate">
          /{protocol.shorthand}
        </TypographyCaptionBold>
        <TypographyCaption className="truncate text-text-secondary">
          {protocol.name}
        </TypographyCaption>
      </div>
    ),
  }));

  return {
    name: 'protocols',
    title: 'PROTOCOLS',
    commands: protocolCommands,
  };
}
